import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Grid, Chip, Stack, LinearProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Tabs, Tab,
  Table, TableHead, TableRow, TableCell,
  IconButton, InputAdornment, Rating,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import SearchIcon from '@mui/icons-material/Search';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import RateReviewIcon from '@mui/icons-material/RateReview';

import {
  VENDORS_QUERY,
  VENDOR_REVIEWS_QUERY,
  MY_VENDOR_BOOKINGS_QUERY,
  BOOK_VENDOR_MUTATION,
  ADD_VENDOR_REVIEW_MUTATION,
  CANCEL_VENDOR_BOOKING_MUTATION,
  MY_CEREMONIES_QUERY,
} from '../graphql/queries';
import { useLov } from '../hooks/useLov';
import ContractedOfferingsPanel from '../components/ContractedOfferingsPanel';
import RegisteredVendorDirectory from '../components/RegisteredVendorDirectory';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const STATUS_COLORS = { pending: 'warning', confirmed: 'success', completed: 'default', cancelled: 'error' };

// ── Vendor Reviews Dialog ─────────────────────────────────────
function ReviewsDialog({ vendor, open, onClose }) {
  const [rating, setRating]           = useState(0);
  const [reviewText, setReviewText]   = useState('');
  const { data, refetch } = useQuery(VENDOR_REVIEWS_QUERY, { variables: { vendorId: vendor?.id }, skip: !vendor?.id });
  const [addReview] = useMutation(ADD_VENDOR_REVIEW_MUTATION, {
    onCompleted: () => { setRating(0); setReviewText(''); refetch(); }
  });

  const reviews = data?.vendorReviews || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reviews — {vendor?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Write a review */}
          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Write a Review</Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="body2">Rating:</Typography>
              <Rating value={rating} onChange={(_, v) => setRating(v)} />
            </Stack>
            <TextField label="Your review" value={reviewText} onChange={e => setReviewText(e.target.value)} fullWidth multiline rows={2} size="small" />
            <Button variant="contained" size="small" sx={{ mt: 1 }}
              onClick={() => addReview({ variables: { vendorId: vendor?.id, rating, reviewText: reviewText || null } })}
              disabled={!rating}>Submit</Button>
          </Box>

          {/* Existing reviews */}
          {reviews.length === 0 && (
            <Typography variant="body2" color="text.secondary">No reviews yet. Be the first!</Typography>
          )}
          {reviews.map(r => (
            <Box key={r.id} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              <Stack direction="row" justifyContent="space-between">
                <Rating value={r.rating} readOnly size="small" />
                <Typography variant="caption" color="text.secondary">
                  {r.reviewer?.email} · {r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN') : ''}
                </Typography>
              </Stack>
              {r.reviewText && <Typography variant="body2" sx={{ mt: 0.5 }}>{r.reviewText}</Typography>}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── Vendor Card ───────────────────────────────────────────────
function VendorCard({ vendor, onBook, onReview }) {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>{vendor.name}</Typography>
        <Chip label={vendor.vendorType?.replace('_', ' ')} size="small" variant="outlined" color="secondary" sx={{ mb: 1 }} />
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <LocationOnIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">{vendor.city}{vendor.state ? `, ${vendor.state}` : ''}</Typography>
        </Stack>
        {(vendor.priceMin || vendor.priceMax) && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            ₹{vendor.priceMin ? Number(vendor.priceMin).toLocaleString('en-IN') : ''}
            {vendor.priceMin && vendor.priceMax ? ' – ' : ''}
            {vendor.priceMax ? Number(vendor.priceMax).toLocaleString('en-IN') : ''}
            {vendor.pricingModel ? ` (${vendor.pricingModel.replace('_', ' ')})` : ''}
          </Typography>
        )}
        {vendor.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}
            style={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {vendor.description}
          </Typography>
        )}
        {vendor.contactPhone && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2">{vendor.contactPhone}</Typography>
          </Stack>
        )}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
          <StarIcon fontSize="small" sx={{ color: 'gold' }} />
          <Typography variant="body2">
            {vendor.averageRating ? Number(vendor.averageRating).toFixed(1) : 'No rating'} · {vendor.reviewCount} review{vendor.reviewCount !== 1 ? 's' : ''}
          </Typography>
        </Stack>
      </CardContent>
      <CardActions>
        <Button size="small" variant="contained" startIcon={<BookmarkAddIcon />} onClick={() => onBook(vendor)}>Book</Button>
        <Button size="small" startIcon={<RateReviewIcon />} onClick={() => onReview(vendor)}>Reviews</Button>
        {vendor.contactEmail && <Button size="small" href={`mailto:${vendor.contactEmail}`} target="_blank">Contact</Button>}
      </CardActions>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function VendorsPage() {
  const [tab, setTab]         = useState(0);
  const [offeringSearch, setOfferingSearch] = useState(''); // set from a directory card
  const [filters, setFilters] = useState({ city: '', vendorType: '' });
  const [search, setSearch]   = useState({ city: '', vendorType: '' });
  const [bookVendor, setBookVendor]     = useState(null);
  const [reviewVendor, setReviewVendor] = useState(null);
  const [bookForm, setBookForm]         = useState({ ceremonyId: '', eventDate: '', agreedPrice: '', notes: '' });

  const { options: vendorTypeOpts } = useLov('vendor_type');

  const { data: vendorData, loading, error } = useQuery(VENDORS_QUERY, {
    variables: { city: search.city || undefined, vendorType: search.vendorType || undefined },
    fetchPolicy: 'cache-and-network',
  });
  const { data: bookingsData, loading: bookingLoading, refetch: refetchBookings } = useQuery(MY_VENDOR_BOOKINGS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: ceremonyData } = useQuery(MY_CEREMONIES_QUERY);

  const [doBook]        = useMutation(BOOK_VENDOR_MUTATION,         { onCompleted: () => { setBookVendor(null); refetchBookings(); } });
  const [cancelBooking] = useMutation(CANCEL_VENDOR_BOOKING_MUTATION, { onCompleted: refetchBookings });

  const vendors  = vendorData?.vendors || [];
  const bookings = bookingsData?.myVendorBookings || [];
  const cerems   = ceremonyData?.myCeremonies || [];

  const handleBook = () => {
    doBook({
      variables: {
        vendorId:    bookVendor.id,
        ceremonyId:  bookForm.ceremonyId  || null,
        eventDate:   bookForm.eventDate,
        agreedPrice: bookForm.agreedPrice ? parseFloat(bookForm.agreedPrice) : null,
        notes:       bookForm.notes       || null,
      }
    });
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>Vendors</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Registered vendors with a signed contract, and the services they offer. Search or pick a service to narrow the list.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Registered vendors" />
        <Tab label="Vendor directory" />
        <Tab label={`Directory bookings (${bookings.length})`} />
      </Tabs>

      {/* Registered (contracted) vendors: every service, with search and a Service filter */}
      {tab === 0 && <ContractedOfferingsPanel key={offeringSearch} initialSearch={offeringSearch} title="Registered vendors" showFilters />}

      {/* Directory Tab: registered vendors (one card each), then vendors an admin added by hand */}
      {tab === 1 && (
        <Box>
          <RegisteredVendorDirectory onViewOfferings={(v) => { setOfferingSearch(v.businessName); setTab(0); }} />

          <Typography variant="h6" fontWeight={700} sx={{ mt: 4, mb: 1 }}>Added by admin</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Vendors an admin listed without a contract.
          </Typography>
          <Card sx={{ mb: 3, p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField label="City" value={filters.city}
                  onChange={e => setFilters(f => ({ ...f, city: e.target.value }))} fullWidth size="small"
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField select label="Vendor Type" value={filters.vendorType}
                  onChange={e => setFilters(f => ({ ...f, vendorType: e.target.value }))} fullWidth size="small">
                  <MenuItem value="">All Types</MenuItem>
                  {vendorTypeOpts.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button variant="contained" fullWidth onClick={() => setSearch({ ...filters })}>Search</Button>
              </Grid>
            </Grid>
          </Card>

          {loading && <LinearProgress />}
          {error   && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
          {!loading && vendors.length === 0 && (
            <Alert severity="info">None yet. Admins can add vendors here without a contract (Admin &gt; Vendors &gt; Vendor directory).</Alert>
          )}

          <Grid container spacing={2}>
            <Paged items={vendors}>{rows => rows.map(v => (
              <Grid item xs={12} sm={6} md={4} key={v.id}>
                <VendorCard vendor={v} onBook={setBookVendor} onReview={setReviewVendor} />
              </Grid>
            ))}</Paged>
          </Grid>
        </Box>
      )}

      {/* Directory bookings Tab */}
      {tab === 2 && (
        <Box>
          {bookingLoading && <LinearProgress />}
          {bookings.length === 0 && !bookingLoading && (
            <Alert severity="info">No vendor bookings yet.</Alert>
          )}
          {bookings.length > 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Ceremony</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Agreed Price (₹)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.map(b => (
                  <TableRow key={b.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{b.vendor?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{b.vendor?.city}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={b.vendor?.vendorType} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{b.ceremony?.name || '—'}</TableCell>
                    <TableCell>{b.eventDate ? new Date(b.eventDate).toLocaleDateString('en-IN') : '—'}</TableCell>
                    <TableCell>{b.agreedPrice ? `₹${Number(b.agreedPrice).toLocaleString('en-IN')}` : '—'}</TableCell>
                    <TableCell><Chip label={b.status} size="small" color={STATUS_COLORS[b.status] || 'default'} /></TableCell>
                    <TableCell>
                      {b.status !== 'cancelled' && b.status !== 'completed' && (
                        <IconButton size="small" color="error"
                          onClick={() => window.confirm('Cancel this booking?') && cancelBooking({ variables: { id: b.id } })}>
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      )}

      {/* Book Dialog */}
      <Dialog open={Boolean(bookVendor)} onClose={() => setBookVendor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Book — {bookVendor?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {cerems.length > 0 && (
              <TextField select label="Link to Ceremony (optional)" value={bookForm.ceremonyId}
                onChange={e => setBookForm(f => ({ ...f, ceremonyId: e.target.value }))} fullWidth>
                <MenuItem value="">— No ceremony —</MenuItem>
                {cerems.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            )}
            <TextField label="Event Date" type="date" value={bookForm.eventDate}
              onChange={e => setBookForm(f => ({ ...f, eventDate: e.target.value }))} fullWidth required InputLabelProps={{ shrink: true }} />
            <TextField label="Agreed Price (₹)" type="number" value={bookForm.agreedPrice}
              onChange={e => setBookForm(f => ({ ...f, agreedPrice: e.target.value }))} fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            <TextField label="Notes" value={bookForm.notes}
              onChange={e => setBookForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookVendor(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleBook} disabled={!bookForm.eventDate}>Confirm Booking</Button>
        </DialogActions>
      </Dialog>

      {/* Reviews Dialog */}
      <ReviewsDialog vendor={reviewVendor} open={Boolean(reviewVendor)} onClose={() => setReviewVendor(null)} />
    </Box>
  );
}
